import type {
  CompetitorBenchmarkDto,
  GooglePlaceDto,
} from '@up2stay/up2promo-api-client';
import { Alert, AlertDescription, Card, Skeleton } from '@up2stay/up2promo-ui';
import { Info, X } from 'lucide-react';
import type { CompetitorNearbyPlaceDto } from '@up2stay/up2promo-api-client';

import { competitorDisplayNames } from '@/core/components/reputation/competitors/competitor-display-names';
import { SuggestionRows } from '@/core/components/reputation/competitors/competitor-suggestions-card';

import { LocationSearchCombobox } from '@/core/components/locations/location-search-combobox';
import {
  HEALTH_ATTENTION_CHIP_CLASS,
  MetaChip,
} from '@/core/components/reputation/reputation-bits';
import {
  REPUTATION_INFO_ALERT_CLASS,
  REPUTATION_INFO_ALERT_TEXT_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type Dict = Dictionary['reputation']['competitorsPage']['tracked'];

/** Scroll to the tracked-competitors card and focus its search field. */
export function focusCompetitorSearch() {
  const card = document.getElementById('competitor-tracked');
  card?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  window.setTimeout(() => card?.querySelector('input')?.focus(), 350);
}

/** Tracked competitors as removable chips, with manual Google search to add more. */
export function CompetitorTrackedCard({
  competitors,
  isLoading,
  atLimit,
  isAdding,
  isRemoving,
  dict,
  notice,
  locale,
  suggestions,
  suggestionsEnabled,
  suggestionsDict,
  onTrackSuggestion,
  onFocusSuggestion,
  onAdd,
  onRemove,
  onSelect,
}: {
  competitors: CompetitorBenchmarkDto[];
  isLoading: boolean;
  atLimit: boolean;
  isAdding: boolean;
  isRemoving: boolean;
  dict: Dict;
  /** Why automatic discovery is unavailable, when it is. */
  notice?: string;
  locale: string;
  /** Nearby businesses not tracked yet (empty when at the limit / none found). */
  suggestions: CompetitorNearbyPlaceDto[];
  /** Nearby discovery works for this location (else the switch is hidden). */
  suggestionsEnabled: boolean;
  suggestionsDict: Dictionary['reputation']['competitorsPage']['suggestions'];
  onTrackSuggestion: (place: CompetitorNearbyPlaceDto) => void;
  onFocusSuggestion: (place: CompetitorNearbyPlaceDto) => void;
  onAdd: (place: GooglePlaceDto) => void;
  onRemove: (id: string) => void;
  onSelect: (competitor: CompetitorBenchmarkDto) => void;
}) {
  const displayNames = competitorDisplayNames(competitors);
  return (
    <Card
      id="competitor-tracked"
      className="flex min-w-0 flex-col gap-3.5 p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={REPUTATION_SECTION_TITLE_CLASS}>
          {dict.title}{' '}
          <span className="text-muted-foreground font-mono text-[13px] font-medium">
            {competitors.length}
          </span>
        </h2>
        <div className="w-full sm:w-[340px]">
          <LocationSearchCombobox
            onSelect={onAdd}
            placeholder={dict.searchPlaceholder}
            disabled={isAdding || atLimit}
            establishmentPoiOnly
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-11 w-full rounded-full" />
      ) : competitors.length === 0 ? (
        <p className="text-muted-foreground text-[13.5px]">{dict.empty}</p>
      ) : (
        <ul className="flex flex-wrap gap-2.5">
          {competitors.map((competitor) => (
            <li
              key={competitor.id}
              className="flex items-center gap-2.5 rounded-full border py-1.5 pl-3.5 pr-2 text-[13px]"
            >
              <button
                type="button"
                className="flex min-w-0 items-center gap-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => onSelect(competitor)}
              >
                <span className="truncate font-semibold">
                  {displayNames.get(competitor.id) ?? competitor.name}
                </span>
                {competitor.rating != null ? (
                  <span className="text-muted-foreground shrink-0">
                    ★ {competitor.rating.toFixed(1)}
                    {competitor.reviewCount != null
                      ? ` · ${competitor.reviewCount.toLocaleString(locale)}`
                      : ''}
                  </span>
                ) : null}
                {competitor.pollStatus === 'stale' ? (
                  <MetaChip className={HEALTH_ATTENTION_CHIP_CLASS}>
                    {dict.stale}
                  </MetaChip>
                ) : null}
              </button>
              <button
                type="button"
                aria-label={`${dict.remove}: ${displayNames.get(competitor.id) ?? competitor.name}`}
                disabled={isRemoving}
                onClick={() => onRemove(competitor.id)}
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <X className="size-[13px]" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {atLimit ? (
        <p className="text-muted-foreground text-xs">{dict.limitReached}</p>
      ) : null}

      {suggestionsEnabled && !atLimit ? (
        <div className="flex flex-col gap-2 border-t pt-3.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[13.5px] font-semibold">
                {suggestionsDict.title}
              </p>
              <p className="text-muted-foreground text-xs">
                {suggestionsDict.intro}
              </p>
            </div>
          </div>
          {competitors.length > 0 ? (
            suggestions.length > 0 ? (
              <SuggestionRows
                suggestions={suggestions}
                isAdding={isAdding}
                dict={suggestionsDict}
                locale={locale}
                onTrack={onTrackSuggestion}
                onFocus={onFocusSuggestion}
              />
            ) : (
              <p className="text-muted-foreground text-xs">
                {suggestionsDict.noMore}
              </p>
            )
          ) : null}
        </div>
      ) : null}
      {notice ? (
        <Alert variant="info" className={REPUTATION_INFO_ALERT_CLASS}>
          <Info aria-hidden />
          <AlertDescription className={REPUTATION_INFO_ALERT_TEXT_CLASS}>
            {notice}
          </AlertDescription>
        </Alert>
      ) : null}
    </Card>
  );
}
